import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL, fmtDate } from "@/lib/format";
import { DateInput } from "@/components/DateInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BadgePercent, Loader2, Trash2, Plus, Download } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";
const PRODUCTS = ["RON95", "DIESEL"];
const BUDI_PRICE = { RON95: 1.99, DIESEL: 2.10 };

export default function Budi() {
  const { stationId, setStationId: setStation, stations } = useStation();
  const station = stationId === "all" ? (stations[0]?.id || "") : stationId;
  const [month, setMonth] = useState("");
  const [weekBasis, setWeekBasis] = useState("mon");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: "", product: "RON95", budi_sales: "" });
  const [saving, setSaving] = useState(false);
  const [pumpPrices, setPumpPrices] = useState({});

  useEffect(() => {
    api.get("/data-months").then((r) => { const m = r.data || []; setMonth(m.length ? m[m.length - 1] : new Date().toISOString().slice(0, 7)); });
  }, []);

  useEffect(() => {
    if (!station) return;
    api.get(`/stations/${station}/pumps`).then((r) => {
      const p = {};
      r.data.forEach((pump) => { p[(pump.product || "").toUpperCase()] = pump.retail_price; });
      setPumpPrices(p);
    }).catch(() => setPumpPrices({}));
  }, [station]);

  const load = () => {
    if (!month) return;
    setLoading(true);
    const q = `month=${month}&week_basis=${weekBasis}${station ? `&station_id=${station}` : ""}`;
    api.get(`/budi?${q}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [station, month, weekBasis]);

  const submit = async (e) => {
    e.preventDefault();
    if (!station) return toast.error("Select a station first");
    setSaving(true);
    try {
      await api.post("/budi", { station_id: station, date: form.date, product: form.product, budi_sales: Number(form.budi_sales || 0) });
      toast.success("BUDI entry saved");
      setForm({ date: "", product: form.product, budi_sales: "" });
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };

  const del = async (id) => {
    try { await api.delete(`/budi/${id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/budi/export?month=${month}&week_basis=${weekBasis}${station ? `&station_id=${station}` : ""}`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = `budi-claims-${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success("BUDI claims exported");
    } catch { toast.error("Export failed"); }
  };

  const t = data?.totals;
  const budiPrice = BUDI_PRICE[form.product] || 0;
  const previewL = form.budi_sales && budiPrice ? Number(form.budi_sales) / budiPrice : 0;
  const curRetail = pumpPrices[form.product];
  const previewClaim = curRetail != null ? Math.max(0, curRetail - budiPrice) * previewL : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Subsidy · Budi95</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <BadgePercent className="w-8 h-8 text-emerald-400" /> BUDI Claims
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">BUDI price RM1.99 (RON95) · RM2.10 (Diesel). Claim = weekly market price − BUDI price, per litre.</p>
        </div>
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Month</Label>
            <Input type="month" data-testid="budi-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Station</Label>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger data-testid="budi-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-44"><SelectValue placeholder="Station" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Claim week</Label>
            <Select value={weekBasis} onValueChange={setWeekBasis}>
              <SelectTrigger data-testid="budi-week-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-36"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="mon">Mon–Sun</SelectItem>
                <SelectItem value="wed">Wed–Tue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={exportXlsx} data-testid="budi-export-button"
            className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {/* Entry form */}
      <div className={`${card} p-5 sm:p-6`}>
        <h3 className="font-[Outfit] font-semibold text-lg mb-3">Record BUDI cash sales</h3>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="budi-form">
          <div><Label className="text-[#9CA3AF]">Date</Label>
            <DateInput required data-testid="budi-date-input" value={form.date} onChange={(iso) => setForm({ ...form, date: iso })} className={inp} /></div>
          <div><Label className="text-[#9CA3AF]">Product</Label>
            <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
              <SelectTrigger data-testid="budi-product-select" className={inp}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                {PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-[#9CA3AF]">Cash Sales (RM) @ BUDI price</Label>
            <Input type="number" step="0.01" required data-testid="budi-sales-input" value={form.budi_sales} onChange={(e) => setForm({ ...form, budi_sales: e.target.value })} className={inp} />
            <div className="text-[11px] mt-1 flex flex-wrap gap-x-3" data-testid="budi-liters-preview">
              <span className="text-[#9CA3AF]">≈ <span className="text-white">{fmtL(previewL)}</span> @ RM{budiPrice.toFixed(2)}/L</span>
              <span className="text-emerald-400">Claim: {previewClaim != null ? fmtMYR(previewClaim) : "—"}{curRetail != null ? ` @ RM${Number(curRetail).toFixed(2)}/L` : " (set pump price)"}</span>
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="budi-save-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Save
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={`${card} p-5`} data-testid="budi-total-liters">
              <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">Total BUDI Litres</div>
              <div className="metric-value text-2xl font-bold text-white mt-1">{fmtL(t?.liters || 0)}</div>
            </div>
            <div className={`${card} p-5`} data-testid="budi-total-sales">
              <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">BUDI Sales (subsidised)</div>
              <div className="metric-value text-2xl font-bold text-blue-400 mt-1">{fmtMYR(t?.budi_sales || 0)}</div>
            </div>
            <div className={`${card} p-5`} data-testid="budi-total-claim">
              <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">Subsidy Claimable</div>
              <div className="metric-value text-2xl font-bold text-emerald-400 mt-1">{fmtMYR(t?.subsidy_claim || 0)}</div>
            </div>
          </div>

          {/* Weekly claims */}
          <div className={`${card} p-5 sm:p-6`}>
            <h3 className="font-[Outfit] font-semibold text-lg mb-1">Weekly Claims</h3>
            <p className="text-xs text-[#9CA3AF] mb-4">Subsidy grouped by claim week ({weekBasis === "wed" ? "Wed–Tue" : "Mon–Sun"}).</p>
            {data?.weekly?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="budi-weekly-table">
                  <thead>
                    <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                      {["week", "budi litres", "budi sales", "subsidy claim"].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.weekly.map((w) => (
                      <tr key={w.week_start} className="border-b border-[#1F2937]/60">
                        <td className="py-2.5 pr-4 metric-value text-white/80">{fmtDate(w.week_start)} → {fmtDate(w.week_end)}</td>
                        <td className="py-2.5 pr-4 metric-value">{fmtL(w.liters)}</td>
                        <td className="py-2.5 pr-4 metric-value text-blue-400">{fmtMYR(w.budi_sales)}</td>
                        <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(w.subsidy_claim)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-[#9CA3AF] py-6 text-center">No BUDI entries this month.</div>}
          </div>

          {/* Daily reconciliation */}
          <div className={`${card} p-5 sm:p-6`}>
            <h3 className="font-[Outfit] font-semibold text-lg mb-4">Daily BUDI Reconciliation</h3>
            {data?.daily?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="budi-daily-table">
                  <thead>
                    <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                      {["date", "product", "budi litres", "budi price", "budi sales", "market price", "subsidy /L", "claim", ""].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily.map((d) => (
                      <tr key={d.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                        <td className="py-2.5 pr-4 metric-value text-white/80">{fmtDate(d.date)}</td>
                        <td className="py-2.5 pr-4">{d.product}</td>
                        <td className="py-2.5 pr-4 metric-value">{fmtL(d.liters)}</td>
                        <td className="py-2.5 pr-4 metric-value">{fmtMYR(d.budi_price)}</td>
                        <td className="py-2.5 pr-4 metric-value text-blue-400">{fmtMYR(d.budi_sales)}</td>
                        <td className="py-2.5 pr-4 metric-value">{d.market_price != null ? fmtMYR(d.market_price) : "—"}</td>
                        <td className="py-2.5 pr-4 metric-value">{d.subsidy_per_l != null ? fmtMYR(d.subsidy_per_l) : "—"}</td>
                        <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(d.subsidy_claim)}</td>
                        <td className="py-2.5 pr-2">
                          <button onClick={() => del(d.id)} data-testid={`budi-delete-${d.date}-${d.product}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-[#9CA3AF] py-6 text-center">No BUDI entries this month.</div>}
          </div>
        </>
      )}
    </div>
  );
}
